/**
 * TimeToResolutionHistogram — Wave 6.2 RESOLUTION-METRICS-KPI
 *
 * Overlapping bucket histograms for the four terminal codes.
 * Surfaces bimodal distributions ("Cancelled in <2 min vs Cancelled at 4h"
 * reveals different abandonment causes).
 *
 * p50 + p95 marker lines render per code. Click a bucket to drill into the
 * matching cart-list filter.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type {
  CartResolutionTimeBundle,
  CartResolutionTimeStats,
  CartResolutionRange,
} from '@retail-ops/shared-types';

interface TimeToResolutionHistogramProps {
  range: CartResolutionRange;
  bundle: CartResolutionTimeBundle;
  branchFilter?: string;
}

const SERIES = [
  { key: 'processed' as const, label: 'Processed', color: 'var(--pcb-green)' },
  { key: 'withdrawn' as const, label: 'Withdrawn', color: 'var(--pcb-amber-text)' },
  { key: 'adverseAction' as const, label: 'Adverse-Action', color: 'var(--pcb-red)' },
  { key: 'cancelled' as const, label: 'Cancelled', color: 'var(--ink-500)' },
];

const CHART_HEIGHT = 180;
const LEFT_PAD = 28;
const RIGHT_PAD = 12;
const TOP_PAD = 18;
const BOTTOM_PAD = 36;

export function TimeToResolutionHistogram({
  range,
  bundle,
  branchFilter,
}: TimeToResolutionHistogramProps): JSX.Element {
  const navigate = useNavigate();

  const buckets = bundle.processed.histogram.map((b) => b.bucket);
  const maxN = useMemo(() => {
    let m = 0;
    for (const s of SERIES) {
      const stats = bundle[s.key];
      for (const b of stats.histogram) if (b.n > m) m = b.n;
    }
    return m;
  }, [bundle]);

  const totalAll = useMemo(() => {
    return SERIES.reduce((sum, s) => sum + bundle[s.key].count, 0);
  }, [bundle]);

  if (totalAll === 0) {
    return (
      <div style={{ padding: 'var(--sp-7)', fontSize: 12, color: 'var(--ink-600)' }}>
        No resolved carts in window — histogram empty.
      </div>
    );
  }

  const innerWidth = 720 - LEFT_PAD - RIGHT_PAD;
  const innerHeight = CHART_HEIGHT - TOP_PAD - BOTTOM_PAD;
  const bucketWidth = innerWidth / buckets.length;
  const barWidth = bucketWidth / SERIES.length - 2;

  function drill(resolution: string, bucket: string): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('resolution', resolution);
    params.set('ttrBucket', bucket);
    if (branchFilter && branchFilter !== 'all') params.set('branch', branchFilter);
    navigate(`/metrics/carts?${params.toString()}`);
  }

  return (
    <div className="ttr-histogram">
      <svg
        viewBox={`0 0 720 ${CHART_HEIGHT}`}
        preserveAspectRatio="xMinYMin meet"
        width="100%"
        height={CHART_HEIGHT}
        role="img"
        aria-label="Time-to-resolution histograms"
      >
        {/* y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = TOP_PAD + innerHeight * (1 - t);
          return (
            <g key={t}>
              <line
                x1={LEFT_PAD}
                x2={720 - RIGHT_PAD}
                y1={y}
                y2={y}
                stroke="var(--ink-100)"
                strokeWidth={1}
              />
              <text
                x={LEFT_PAD - 4}
                y={y + 3}
                fontSize={9}
                fill="var(--ink-500)"
                textAnchor="end"
              >
                {Math.round(t * maxN)}
              </text>
            </g>
          );
        })}

        {/* bars */}
        {buckets.map((bucket, bi) => {
          const xBase = LEFT_PAD + bi * bucketWidth;
          return (
            <g key={bucket}>
              {SERIES.map((s, si) => {
                const stat = bundle[s.key];
                const point = stat.histogram[bi];
                const n = point?.n ?? 0;
                const h = maxN > 0 ? (n / maxN) * innerHeight : 0;
                const x = xBase + si * (barWidth + 2) + 1;
                const y = TOP_PAD + innerHeight - h;
                return (
                  <rect
                    key={s.key}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={h}
                    fill={s.color}
                    style={{ cursor: n > 0 ? 'pointer' : 'default', opacity: n === 0 ? 0.15 : 1 }}
                    onClick={n > 0 ? () => drill(s.label, bucket) : undefined}
                  >
                    <title>{`${s.label} · ${bucket}: ${n}`}</title>
                  </rect>
                );
              })}
              <text
                x={xBase + bucketWidth / 2}
                y={CHART_HEIGHT - 18}
                fontSize={9}
                textAnchor="middle"
                fill="var(--ink-700)"
              >
                {bucket}
              </text>
            </g>
          );
        })}
      </svg>

      {/* per-series percentile callouts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 'var(--sp-4)',
          fontSize: 11,
          color: 'var(--ink-700)',
          marginTop: 'var(--sp-4)',
        }}
      >
        {SERIES.map((s) => {
          const stat = bundle[s.key];
          return (
            <div
              key={s.key}
              style={{
                padding: 'var(--sp-3) var(--sp-4)',
                background: 'var(--ink-50)',
                borderRadius: 'var(--r-md)',
                borderLeft: `3px solid ${s.color}`,
              }}
            >
              <div style={{ fontWeight: 700, color: 'var(--ink-900)', fontSize: 11 }}>{s.label}</div>
              <div style={{ marginTop: 2 }}>
                n={stat.count} · p50 {fmt(stat.p50)} · p95 {fmt(stat.p95)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function fmt(min: number | null): string {
  if (min == null) return '–';
  if (min < 60) return `${Math.round(min)}m`;
  if (min < 1440) return `${(min / 60).toFixed(1)}h`;
  return `${(min / 1440).toFixed(1)}d`;
}

// Re-export for InefficiencyHotspots prop typing convenience.
export type { CartResolutionTimeStats };
